import type {
	ApiErrorType,
	ApiInfiniteQueryData,
	EndpointPageParams,
	PaginatedRequest,
	SMBPaginatedRequest,
} from "@layer/types/api";
import type { InfiniteData } from "@tanstack/react-query";

/**
 * Appends a new item to the last page of a paginated data.
 * This function assumes that the data is paginated with multiple "pages",
 *
 * @param prev - The current paginated data
 * @param item - The new item to be added to the last page.
 * @notice - use it for Infinite Query Only
 */
export const appendToInfiniteQuery = <T>(
	prev: ApiInfiniteQueryData<T> | undefined,
	item: T,
) => {
	return prev
		? {
				...prev,
				pages: prev?.pages.map((page, index) =>
					index === prev.pages.length - 1
						? { ...page, data: [...(page?.data ?? []), item] }
						: page,
				),
			}
		: undefined;
};

/**
 * Removes an item from the pages of a paginated data.
 *
 * This function assumes that the data is paginated with multiple "pages",
 *
 * @param prev - The current paginated data
 * @param removeId - The ID of the item to be removed.
 * @param removeProperyName - The property name that should be used to match the item for updating (e.g., 'beneficiaryId').
 * @notice - use it for Infinite Query Only
 */
export const removeFromInfiniteQuery = <T>(
	prev: ApiInfiniteQueryData<T> | undefined,
	removeId: string,
	removeProperyName: keyof T,
) => {
	return prev
		? {
				...prev,
				pages: prev.pages.map((page) => ({
					...page,
					data: page?.data.filter(
						(item: T) => item[removeProperyName] !== removeId,
					),
				})),
			}
		: undefined;
};

/**
 * Updates an item in the pages of a paginated data.
 *
 * This function assumes that the data is paginated with multiple "pages",
 *
 * @param prev - The current paginated data (contains an array of pages).
 * @param updateItem - The new item that should replace the old one.
 * @param updateId - The ID of the item to be updated.
 * @param updatePropertyName - The property name that should be used to match the item for updating (e.g., 'beneficiaryId').
 * @notice - use it for Infinite Query Only
 */
export const updateFromInfiniteQuery = <T>(
	prev: ApiInfiniteQueryData<T> | undefined,
	updateItem: T,
	updateId: string,
	updatePropertyName: keyof T,
) => {
	return prev
		? {
				...prev,
				pages: prev.pages.map((page) => ({
					...page,
					data: page?.data.map((item: T) =>
						item[updatePropertyName] === updateId
							? updateItem
							: item,
					),
				})),
			}
		: undefined;
};

export const flattenPaginatedPages = <T>(
	data: InfiniteData<SMBPaginatedRequest<T> | undefined, unknown> | undefined,
): T[] => {
	if (!data) return [];
	return data.pages.flatMap((page) => page?.data ?? []);
};

type GenerateMockResponseResult<T, TPaginated extends boolean> = {
	data?: TPaginated extends true ? SMBPaginatedRequest<T> : T[] | undefined;
} & ApiErrorType;

export const generateMockResponse = <T, TPaginated extends boolean>({
	mockData,
	isPaginated,
	pagingToken,
	pageSize,
}: EndpointPageParams & {
	mockData: Array<T>;
	isPaginated: TPaginated;
}): GenerateMockResponseResult<T, TPaginated> => {
	try {
		if (!isPaginated) {
			return {
				data: mockData,
			} as GenerateMockResponseResult<T, TPaginated>;
		}

		const size = pageSize ?? 5;
		const startIndex = pagingToken ? Number.parseInt(pagingToken, 10) : 0;
		const safeStartIndex = Number.isNaN(startIndex) ? 0 : startIndex;

		const items = mockData.slice(safeStartIndex, safeStartIndex + size);

		const nextIndex = safeStartIndex + size;
		const nextPageToken =
			nextIndex < mockData.length ? String(nextIndex) : "";

		return {
			data: {
				data: items,
				paging: {
					nextToken: nextPageToken,
				},
			},
		} as GenerateMockResponseResult<T, TPaginated>;
	} catch {
		return {
			error: "Failed to generate mock response",
			status: "500",
		} as GenerateMockResponseResult<T, TPaginated>;
	}
};
